import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import {
  getAbp5FileDownloadUrl,
  getAbp5FileJobsByDocument,
  getLatestAbp5FileJob,
  postGenerateAbp5File,
} from 'modules/annual-benefit-planning/modules/approval/infra/api/config.api'
import { APPROVAL_QUERY_KEYS } from 'modules/annual-benefit-planning/modules/approval/infra/react-query/approval.query-keys'
import type { OepTargetFileJob } from '../types'
import {
  type GeneratedWorkbookJobsMessages,
  type GeneratedWorkbookJobsResult,
  isInFlightJob,
  useGeneratedWorkbookJobs,
} from './useGeneratedWorkbookJobs'

export const FILE_INITIAL_REVIEW_UNAVAILABLE_MESSAGE =
  'File Initial Review is not available. Please upload it first'

const ABP5_FILE_MESSAGES: GeneratedWorkbookJobsMessages = {
  loadFailure: 'Failed to load ABP file jobs.',
  generateFailure: 'Failed to generate ABP file. Please try again.',
  downloadFailure: 'Failed to download ABP file. Please try again.',
  generateFailureByStatus: {
    409: 'An ABP file job is already in progress for this client.',
    422: FILE_INITIAL_REVIEW_UNAVAILABLE_MESSAGE,
  },
}

const POLL_INTERVAL_MS = 5000

const EMPTY_JOBS_BY_DOCUMENT: ReadonlyMap<string, OepTargetFileJob> = new Map()

interface Options {
  readonly clientId: string
  /** When false, skips ABP-5 job queries (feature flag off). Default true. */
  readonly enabled?: boolean
}

export interface UseAbp5FileJobsResult extends GeneratedWorkbookJobsResult {
  /** Latest ABP-5 job keyed by analysis documentId. */
  readonly jobsByDocumentId: ReadonlyMap<string, OepTargetFileJob>
  readonly isJobsByDocumentLoading: boolean
}

function buildJobsByDocumentId(
  jobs: readonly OepTargetFileJob[] | undefined
): ReadonlyMap<string, OepTargetFileJob> {
  if (jobs === undefined || jobs.length === 0) {
    return EMPTY_JOBS_BY_DOCUMENT
  }
  const map = new Map<string, OepTargetFileJob>()
  for (const job of jobs) {
    if (job.documentId != null && job.documentId.length > 0) {
      map.set(job.documentId, job)
    }
  }
  return map
}

/** ABP-5 generation for one uploaded analysis document. */
export function useAbp5FileJobs({
  clientId,
  enabled = true,
}: Options): UseAbp5FileJobsResult {
  const queryClient = useQueryClient()
  const byDocumentQueryKey =
    APPROVAL_QUERY_KEYS.abp5FileJobsByDocument(clientId)

  const fetchLatestJob = useCallback(
    () => getLatestAbp5FileJob(clientId),
    [clientId]
  )
  const generateJob = useCallback(
    (documentId?: string) => {
      if (documentId === undefined || documentId === '') {
        return Promise.reject(
          new Error('Select an analysis file before generating an ABP file.')
        )
      }
      return postGenerateAbp5File(clientId, documentId)
    },
    [clientId]
  )
  const fetchDownloadUrl = useCallback(
    (jobId: string) => getAbp5FileDownloadUrl(clientId, jobId),
    [clientId]
  )

  const workbookJobs = useGeneratedWorkbookJobs({
    queryKey: APPROVAL_QUERY_KEYS.latestAbp5FileJob(clientId),
    fetchLatestJob,
    generateJob,
    fetchDownloadUrl,
    messages: ABP5_FILE_MESSAGES,
    enabled,
  })

  const { data: byDocumentJobs, isLoading: isJobsByDocumentLoading } = useQuery(
    {
      queryKey: byDocumentQueryKey,
      queryFn: () => getAbp5FileJobsByDocument(clientId),
      enabled,
      refetchInterval: (query) => {
        const jobs = query.state.data
        if (jobs === undefined) return false
        return jobs.some((job) => isInFlightJob(job)) ? POLL_INTERVAL_MS : false
      },
    }
  )

  const jobsByDocumentId = useMemo(
    () => buildJobsByDocumentId(byDocumentJobs),
    [byDocumentJobs]
  )

  const anyByDocumentInFlight = useMemo(
    () =>
      byDocumentJobs?.some(
        (job) =>
          job.documentId != null &&
          job.documentId.length > 0 &&
          isInFlightJob(job)
      ) ?? false,
    [byDocumentJobs]
  )

  const handleGenerate = useCallback(
    async (documentId?: string) => {
      await workbookJobs.handleGenerate(documentId)
      void queryClient.invalidateQueries({ queryKey: byDocumentQueryKey })
    },
    [workbookJobs, queryClient, byDocumentQueryKey]
  )

  return {
    ...workbookJobs,
    handleGenerate,
    isGenerateDisabled:
      workbookJobs.isGenerateDisabled || anyByDocumentInFlight,
    jobsByDocumentId,
    isJobsByDocumentLoading,
  }
}
